// ═══════════════════════════════════════════════════════════════════════════════
// ReactionLibraryTab.js
// ═══════════════════════════════════════════════════════════════════════════════
//
// Admin-only emoji library manager. Renders inside the Reactions tab of
// ManageTemplates. Shows every emoji that can ever appear on a card,
// indicates which are default-assigned to new templates, displays usage
// stats, and lets the admin add/edit/reorder/deactivate/delete.
//
// USAGE
//   <ReactionLibraryTab />
//
// Self-contained. Calls the admin emoji endpoints via adminService.
//
// DATA SHAPE (from getReactionEmojis):
//   {
//     emojiid: 1,
//     emoji_char: "❤️",
//     display_name: "Heart",
//     description: "Love it!",
//     isdefault: true,        // included in default set for new templates
//     isactive: true,         // available to assign
//     display_order: 1,       // sort key for library display
//     usage_count: 42,        // how many card_reactions rows reference it
//     template_count: 12,     // how many templates have it assigned
//   }
//
// BEHAVIOR
//   • Add or edit emoji via a single modal — supports emoji_char, display_name,
//     description, isactive, isdefault. Order is set via drag handles in the
//     list itself, not the modal.
//   • Deactivate confirmation gives the admin two paths:
//       (a) Keep on existing templates (just unavailable for new assignments)
//       (b) Remove from all templates immediately
//   • Reactivate just flips the flag — no confirmation needed.
//   • Delete is hard-deleted only if usage_count === 0 AND template_count === 0.
//     Otherwise blocked with a clear message pointing to deactivate.
//   • Drag-to-reorder updates display_order locally and persists in one call
//     (reorderReactionEmojis), so the admin can rearrange and then "settle"
//     without per-item save clicks.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import adminService from '../../services/adminService.js';

// ─── Helper ──────────────────────────────────────────────────────────────
// Returns the first character of a string, handling surrogate pairs and
// ZWJ sequences for compound emojis like 👨‍👩‍👧 by trusting whatever the
// admin pasted. We don't try to validate "is this an emoji?" — emojis
// evolve faster than any whitelist, and admins are trusted.
const trimmedEmojiChar = (s) => (s || '').trim();

// ─── Library Row ─────────────────────────────────────────────────────────
// One emoji in the library list. Handles its own drag state so reordering
// doesn't force the whole list to re-render on every dragover.
function LibraryRow({
  emoji,
  index,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onEdit,
  onToggleActive,
  onDelete,
}) {
  const canDelete = (parseInt(emoji.usage_count) || 0) === 0
    && (parseInt(emoji.template_count) || 0) === 0;
  const isActive = emoji.isactive !== false;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, index)}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition-all ${
        isDragging
          ? 'border-gold bg-gold/10 cursor-grabbing'
          : isActive
            ? 'border-royal-600 bg-royal-900/40 hover:border-royal-500'
            : 'border-royal-700 bg-royal-900/20 opacity-70'
      }`}
    >
      {/* Drag handle */}
      <span
        className="text-royal-200 cursor-grab select-none px-1"
        aria-label="Drag to reorder"
        title="Drag to reorder"
      >
        ⋮⋮
      </span>

      {/* Emoji preview */}
      <span className="text-3xl leading-none" aria-hidden="true">
        {emoji.emoji_char}
      </span>

      {/* Info column */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-white font-medium text-base">{emoji.display_name}</p>
          {emoji.isdefault && (
            <span className="px-2 py-0.5 bg-gold/15 text-gold text-xs rounded-full">
              Default
            </span>
          )}
          {!isActive && (
            <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 text-xs rounded-full">
              Disabled
            </span>
          )}
        </div>
        {emoji.description && (
          <p className="text-royal-200 text-sm truncate">{emoji.description}</p>
        )}
        <p className="text-royal-200 text-xs mt-0.5">
          {emoji.usage_count || 0} click{emoji.usage_count != 1 ? 's' : ''}
          {' · '}
          {emoji.template_count || 0} template{emoji.template_count != 1 ? 's' : ''}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <button
          className="text-gold text-sm hover:text-gold-light transition-colors"
          onClick={() => onEdit(emoji)}
        >
          Edit
        </button>
        <button
          className={`text-sm transition-colors ${
            isActive ? 'text-amber-400 hover:text-amber-300' : 'text-green-400 hover:text-green-300'
          }`}
          onClick={() => onToggleActive(emoji)}
        >
          {isActive ? 'Deactivate' : 'Reactivate'}
        </button>
        <button
          className={`text-sm transition-colors ${
            canDelete ? 'text-red-400 hover:text-red-300' : 'text-royal-200 opacity-50 cursor-not-allowed'
          }`}
          onClick={() => onDelete(emoji)}
          disabled={!canDelete}
          title={canDelete ? 'Permanently delete' : 'In use — deactivate instead'}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Add/Edit Modal ──────────────────────────────────────────────────────
// Mounted only when active. Self-managed form state; calls onSave with the
// payload to persist. Parent handles the actual create/update call so this
// component stays presentational.
function EmojiEditModal({ emoji, onClose, onSave, saving }) {
  const isNew = !emoji?.emojiid;

  const [form, setForm] = useState({
    emoji_char: emoji?.emoji_char || '',
    display_name: emoji?.display_name || '',
    description: emoji?.description || '',
    isdefault: emoji?.isdefault || false,
    isactive: emoji?.isactive !== false,
  });
  const [error, setError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    const emojiChar = trimmedEmojiChar(form.emoji_char);
    const displayName = (form.display_name || '').trim();

    if (!emojiChar) { setError('Emoji character is required'); return; }
    if (!displayName) { setError('Display name is required'); return; }
    if (emojiChar.length > 16) { setError('Emoji character is too long'); return; }

    onSave({
      emoji_char: emojiChar,
      display_name: displayName,
      description: (form.description || '').trim() || null,
      isdefault: !!form.isdefault,
      isactive: !!form.isactive,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col sm:mx-4">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">
              {isNew ? 'Add Reaction Emoji' : 'Edit Reaction Emoji'}
            </h2>
            <p className="text-royal-200 text-sm">
              {isNew ? 'Add a new reaction option to the library' : 'Update this reaction emoji'}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gold transition-colors text-xl p-2">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Emoji char */}
          <div>
            <label className="block text-white text-sm mb-1.5">
              Emoji character *
            </label>
            <input
              type="text"
              value={form.emoji_char}
              onChange={(e) => setForm({ ...form, emoji_char: e.target.value })}
              placeholder="❤️"
              maxLength={16}
              autoFocus={isNew}
              className="w-full px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-2xl text-white placeholder-royal-200 focus:outline-none focus:border-gold/50"
            />
            <p className="text-royal-200 text-xs mt-1">
              Paste the actual emoji character. Works with most modern emojis including skin-tone modifiers.
            </p>
          </div>

          {/* Display name */}
          <div>
            <label className="block text-white text-sm mb-1.5">
              Display name *
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g. Heart, Thumbs up, Party popper"
              maxLength={100}
              className="w-full px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50"
            />
            <p className="text-royal-200 text-xs mt-1">
              Used as the button's accessibility label and shown to admins selecting reactions.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-white text-sm mb-1.5">
              Description <span className="text-royal-200">(optional)</span>
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Show some love"
              maxLength={255}
              className="w-full px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50"
            />
            <p className="text-royal-200 text-xs mt-1">
              Internal note — never shown to end users.
            </p>
          </div>

          {/* Flags */}
          <div className="space-y-3 pt-2 border-t border-royal-700/40">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isdefault}
                onChange={(e) => setForm({ ...form, isdefault: e.target.checked })}
                className="mt-0.5 w-5 h-5 accent-gold"
              />
              <div>
                <span className="text-white text-base">Include in default set</span>
                <p className="text-royal-200 text-sm">
                  New templates created from now on will automatically include this emoji. Existing templates are unaffected.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isactive}
                onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                className="mt-0.5 w-5 h-5 accent-gold"
              />
              <div>
                <span className="text-white text-base">Active</span>
                <p className="text-royal-200 text-sm">
                  Disable to hide from admin selection lists. Existing template assignments are unaffected.
                </p>
              </div>
            </label>
          </div>
        </form>

        <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t border-royal-700/50 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving...' : (isNew ? 'Add Emoji' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Deactivate Confirmation Modal ───────────────────────────────────────
// Custom confirm because window.confirm doesn't support the two-path
// "keep on templates" vs "remove from all templates" choice.
function DeactivateConfirmModal({ emoji, onClose, onConfirm, saving }) {
  const templateCount = parseInt(emoji.template_count) || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md sm:mx-4 flex flex-col">
        <div className="px-4 sm:px-6 py-4 border-b border-royal-700/50">
          <h2 className="text-amber-400 font-display text-lg sm:text-xl flex items-center gap-2">
            <span aria-hidden="true">⚠️</span>
            <span>Deactivate {emoji.emoji_char} {emoji.display_name}?</span>
          </h2>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-white text-sm sm:text-base">
            This emoji is currently assigned to <span className="text-gold font-semibold">{templateCount}</span> template{templateCount !== 1 ? 's' : ''}.
            Choose how to handle existing assignments:
          </p>

          <button
            onClick={() => onConfirm('keep')}
            disabled={saving}
            className="w-full text-left px-4 py-3 rounded-lg border border-royal-600 bg-royal-800/40 hover:border-gold/50 transition-colors disabled:opacity-60"
          >
            <p className="text-white font-medium">Keep on existing templates</p>
            <p className="text-royal-200 text-sm">
              Cards using these templates will continue to show this emoji as a reaction option. It just won't be available when creating new templates or editing existing ones.
            </p>
          </button>

          <button
            onClick={() => onConfirm('remove')}
            disabled={saving}
            className="w-full text-left px-4 py-3 rounded-lg border border-red-600/40 bg-red-900/20 hover:border-red-400 transition-colors disabled:opacity-60"
          >
            <p className="text-red-400 font-medium">Remove from all templates</p>
            <p className="text-royal-200 text-sm">
              The emoji will immediately disappear from {templateCount} template{templateCount !== 1 ? 's' : ''}.
              Recipients who land on a card after this won't see it. Past reaction counts are preserved in analytics.
            </p>
          </button>

          {templateCount === 0 && (
            <p className="text-royal-200 text-sm italic">
              Not currently assigned to any templates — either option does the same thing.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t border-royal-700/50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Tab ────────────────────────────────────────────────────────────
export default function ReactionLibraryTab() {
  const [emojis, setEmojis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add/Edit modal state
  const [editing, setEditing] = useState(null);  // emoji obj or {} for "new"
  const [saving, setSaving] = useState(false);

  // Deactivate confirm modal state
  const [deactivating, setDeactivating] = useState(null);

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  // Persist order to server when reorder settles (debounced — see effect)
  const orderDirtyRef = useRef(false);
  const debounceRef = useRef(null);

  // ─── Initial load ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getReactionEmojis();
      // Server should return them sorted by display_order, but we sort
      // defensively just in case (other admins editing concurrently, etc).
      const sorted = (data.emojis || []).slice().sort(
        (a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0)
      );
      setEmojis(sorted);
    } catch (err) {
      setError(err?.data?.error || 'Failed to load emojis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Persist reorder ─────────────────────────────────────────────────
  // After every reorder, schedule a single batched save with the new
  // display order. If the admin keeps dragging, the save is deferred —
  // we coalesce multiple reorders into one network call.
  useEffect(() => {
    if (!orderDirtyRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        // adminService.reorderReactionEmojis takes a flat array of emojiids
        // in the desired order — the server renumbers display_order based
        // on position in the array.
        const orderedIds = emojis.map((e) => e.emojiid);
        await adminService.reorderReactionEmojis(orderedIds);
        orderDirtyRef.current = false;
      } catch (err) {
        // On failure, reload to restore the server's truth.
        console.error('Reorder save failed:', err);
        load();
      }
    }, 700);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [emojis, load]);

  // ─── Drag handlers ───────────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex === null || draggedIndex === index) return;

    // Live-reorder while dragging — feels much better than waiting for drop.
    setEmojis(prev => {
      const next = prev.slice();
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(index);
    orderDirtyRef.current = true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // ─── Edit / Add ──────────────────────────────────────────────────────
  const handleAddNew = () => {
    setEditing({});  // empty object = new emoji
  };

  const handleEdit = (emoji) => {
    setEditing(emoji);
  };

  const handleSaveEdit = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      if (editing.emojiid) {
        await adminService.updateReactionEmoji(editing.emojiid, payload);
      } else {
        await adminService.createReactionEmoji(payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Toggle active ───────────────────────────────────────────────────
  const handleToggleActive = async (emoji) => {
    if (emoji.isactive !== false) {
      // Deactivating — confirm via custom modal
      setDeactivating(emoji);
    } else {
      // Reactivating — straightforward
      try {
        await adminService.reactivateReactionEmoji(emoji.emojiid);
        await load();
      } catch (err) {
        setError(err?.data?.error || 'Reactivate failed');
      }
    }
  };

  const handleDeactivateConfirm = async (mode) => {
    if (!deactivating) return;
    setSaving(true);
    try {
      // adminService.deactivateReactionEmoji takes (id, modeString)
      // and wraps it in { mode } itself before posting.
      await adminService.deactivateReactionEmoji(deactivating.emojiid, mode);
      setDeactivating(null);
      await load();
    } catch (err) {
      setError(err?.data?.error || 'Deactivate failed');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async (emoji) => {
    const ok = window.confirm(
      `Permanently delete ${emoji.emoji_char} ${emoji.display_name}? This cannot be undone.`
    );
    if (!ok) return;
    try {
      await adminService.deleteReactionEmoji(emoji.emojiid);
      await load();
    } catch (err) {
      setError(err?.data?.error || 'Delete failed');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-4 p-4 bg-royal-800/40 border border-royal-600 rounded-lg">
        <p className="text-royal-200 text-sm leading-relaxed">
          <span className="text-gold font-medium">😀 Reaction Emoji Library</span> — manage the master list of emojis recipients can click on cards. Mark some as <span className="text-gold">defaults</span> to auto-include them on new templates. Use the drag handles to set the order they appear on cards.
        </p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-royal-200 text-sm">
          {loading ? 'Loading…' : `${emojis.length} emoji${emojis.length !== 1 ? 's' : ''} in library`}
        </p>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
        >
          + Add Emoji
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      ) : emojis.length === 0 ? (
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-8 text-center">
          <p className="text-gold text-lg font-semibold mb-2">No reaction emojis yet</p>
          <p className="text-royal-200 text-sm mb-4">
            Add at least one emoji here to enable card reactions site-wide. Common starters: ❤️ Heart, 👍 Thumbs up, 🎉 Party popper, 🙏 Praying hands.
          </p>
          <button
            onClick={handleAddNew}
            className="inline-block px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
          >
            + Add First Emoji
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {emojis.map((emoji, i) => (
            <LibraryRow
              key={emoji.emojiid}
              emoji={emoji}
              index={i}
              isDragging={draggedIndex === i}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      {editing && (
        <EmojiEditModal
          emoji={editing}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
          saving={saving}
        />
      )}

      {/* Deactivate confirm modal */}
      {deactivating && (
        <DeactivateConfirmModal
          emoji={deactivating}
          onClose={() => setDeactivating(null)}
          onConfirm={handleDeactivateConfirm}
          saving={saving}
        />
      )}
    </div>
  );
}